import type {GalleryDialogOpenedDetail} from './gallery-dialog.opener.types';
import {GALLERY_DIALOG_OPENED_EVENT} from './gallery-dialog.opener.types';

type GalleryDialogMode = 'default' | 'attach';

/** Who opened the gallery, read off a {@link GALLERY_DIALOG_OPENED_EVENT}; null for an anonymous opener or any other event. */
export const getGalleryDialogOpenerId = (event: Event): string | null => {
  if (!(event instanceof CustomEvent)) {
    return null;
  }

  const detail: unknown = event.detail;
  if (typeof detail !== 'object' || detail === null || !('openerId' in detail) || typeof detail.openerId !== 'string') {
    return null;
  }

  return detail.openerId;
};

export interface GalleryDialogEventsWizardBannerOpts {
  showEventsWizardBanner: boolean;
  shouldUpsellForSemanticTagging: boolean;
}

const GALLERY_MODAL_ID = 'gallery-dialog';
const GALLERY_MODAL_CLASS = 'gallery-modal -full-height js-gallery-dialog';
const SKELETON_TILE_COUNT = 24;
const SIDEBAR_ITEM_COUNT = 16;
const SIDEBAR_ITEM_WIDTHS = ['55%', '70%', '85%', '60%', '75%', '50%', '80%', '65%'];
const TILE_LABEL_WIDTHS = ['40%', '55%', '35%', '60%', '45%', '50%'];

const getGalleryDialogSkeletonHTML = (): string => {
  const sidebarItems = Array.from({length: SIDEBAR_ITEM_COUNT}, (_, i) => {
    const width = SIDEBAR_ITEM_WIDTHS[i % SIDEBAR_ITEM_WIDTHS.length];
    return `<div class="skeleton-loader gallery-skeleton-sidebar-item" style="width:${width}"></div>`;
  }).join('');

  const tiles = Array.from({length: SKELETON_TILE_COUNT}, (_, i) => {
    const labelWidth = TILE_LABEL_WIDTHS[i % TILE_LABEL_WIDTHS.length];
    return (
      '<div class="gallery-skeleton-tile flex-column-justify-start">' +
      '<div class="skeleton-loader gallery-skeleton-tile-image"></div>' +
      `<div class="skeleton-loader gallery-skeleton-tile-label _hidden" style="width:${labelWidth}"></div>` +
      '</div>'
    );
  }).join('');

  return (
    '<div class="gallery-dialog-skeleton">' +
    '<div class="gallery-skeleton-sidebar flex-column-justify-start _overflow-hidden">' +
    '<div class="skeleton-loader gallery-skeleton-sidebar-heading"></div>' +
    sidebarItems +
    '</div>' +
    '<div class="gallery-skeleton-topbar flex-row-justify-between">' +
    '<div class="skeleton-loader gallery-skeleton-search"></div>' +
    '<div class="skeleton-loader gallery-skeleton-dropdown"></div>' +
    '</div>' +
    `<div class="gallery-skeleton-grid">${tiles}</div>` +
    '</div>'
  );
};

export const openGalleryDialog = (
  idFolder: string,
  doEmitHashedIdOnSelect = false,
  galleryPage = '',
  eventsWizardBannerOpts: GalleryDialogEventsWizardBannerOpts = {showEventsWizardBanner: false, shouldUpsellForSemanticTagging: false},
  shouldShowLoadingToast = true,
  skipAuthCheck = false,
  title?: string,
  mode: GalleryDialogMode = 'default',
  openerId: string | null = null
): void => {
  if (!skipAuthCheck && !window.PMW.isUserLoggedIn()) {
    window.PMW.util.redirectToLoginPage(window.location.href);
    return;
  }

  window.dispatchEvent(new CustomEvent<GalleryDialogOpenedDetail>(GALLERY_DIALOG_OPENED_EVENT, {detail: {openerId}}));

  if (shouldShowLoadingToast) {
    window.PMW.setLoading(window.i18next.t('pmwjs_loading'));
  }

  const titleText = title ?? (eventsWizardBannerOpts.showEventsWizardBanner
    ? window.i18next.t('pmwjs_design_templates')
    : window.i18next.t('pmwjs_create_design'));

  const modal = window.PMW.openModalDialog(GALLERY_MODAL_ID, titleText, {}, true, GALLERY_MODAL_CLASS, false, []);
  modal.find('.content').html(getGalleryDialogSkeletonHTML());

  window.PMW.util.require(typeof window.PMW.doOpenGalleryDialog === 'undefined', 'galleryDialog', false, () => {
    window.PMW.doOpenGalleryDialog(idFolder, doEmitHashedIdOnSelect, galleryPage, eventsWizardBannerOpts, title, mode);
  });
};

window.PMW.openGalleryDialog = openGalleryDialog;
window.PMW.getGalleryDialogSkeletonHTML = getGalleryDialogSkeletonHTML;
